import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { LinkComponentProps, useLocation, useNavigate } from '@tanstack/react-router'
import {
  SortingState,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { useCallback } from 'react'
import { CommandPopover, MethodCommandList } from '~/features/command-lists'
import { Table } from '~/features/table/ui/table'
import { useLoader } from '~/modules/loader'
import { useTRPC } from '~/services/trpc'
import {
  SettingsContent,
  SettingsPagination,
  useSettingTitle,
} from '~/templates/settings'

import { FilterCollection } from '@hap/analytics'
import {
  Search,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@hap/components'

import { CHANNEL_FALLBACK_DATA, columns } from './-components/table-config'

export const Route = createFileRoute('/_authenticated/$alias/settings/channels/')({
  component: RouteComponent,
})

const defaultSorting = [{ id: 'name', desc: false }] satisfies SortingState
const pageSize = 50
const title = ['Channels']

function RouteComponent() {
  const location = useLocation()
  const navigate = useNavigate({ from: '/$alias/settings' })
  const trpc = useTRPC()

  useSettingTitle(title)

  // Read state from route search
  const search = location.search as Record<string, unknown>
  const query = (search.query as string) || ''
  const filters = (search.filters as Record<string, unknown>) || {}
  const methods = (filters.methods || []) as string[]
  const ciEnabled = filters.ciEnabled as boolean | null | undefined
  const autoQAEnabled = filters.autoQAEnabled as boolean | null | undefined
  const sort = (search.sort as SortingState) || defaultSorting
  const currentPage = (search.page as number) || 1

  const queryOptions = {
    query,
    filters: {
      methods: methods.length ? methods : undefined,
      ciEnabled: ciEnabled,
      autoQAEnabled: autoQAEnabled,
    },
    pageNumber: currentPage,
    pageSize: pageSize,
    sortField: sort[0]?.id || 'name',
    direction: sort[0]?.desc ? 'desc' : 'asc',
  }

  const { data, isLoading } = useQuery(
    trpc.contactChannels.query.queryOptions(queryOptions),
  )

  const table = useReactTable({
    columns: columns,
    data: data?.page ?? CHANNEL_FALLBACK_DATA,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    enableSortingRemoval: false,
    getRowId: (row) => row.id,
    onSortingChange: (sorter) => {
      const newSort = typeof sorter === 'function' ? sorter(sort) : sorter
      setSort(newSort)
    },
    manualSorting: true,
    state: {
      sorting: sort,
    },
    defaultColumn: {
      size: 0,
      minSize: 0,
      maxSize: Number.MAX_SAFE_INTEGER,
    },
  })

  useLoader(isLoading)

  const total = data?.total || null

  const update = useCallback(
    (updates: Record<string, unknown>) => {
      navigate({
        to: location.pathname,
        search: {
          ...(location.search as Record<string, unknown>),
          ...updates,
        },
        replace: true,
      })
    },
    [location.pathname, location.search, navigate],
  )

  const updateFilters = useCallback(
    (filterUpdates: Record<string, unknown>) => {
      const currentFilters =
        ((location.search as Record<string, unknown>).filters as Record<
          string,
          unknown
        >) || {}

      navigate({
        to: location.pathname,
        search: {
          ...(location.search as Record<string, unknown>),
          filters: { ...currentFilters, ...filterUpdates },
        },
        replace: true,
      })
    },
    [location.pathname, location.search, navigate],
  )

  const setSort = useCallback(
    (nextSort: SortingState) => {
      navigate({
        to: location.pathname,
        search: {
          ...(location.search as Record<string, unknown>),
          sort: nextSort,
        },
        replace: true,
      })
    },
    [location.pathname, location.search, navigate],
  )

  // Function to get row link for navigation
  const getRowLink = useCallback((row: { original: { id: string } }) => {
    const channelId = row.original?.id
    return channelId
      ? ({
          from: '/$alias/settings/channels/',
          to: '/$alias/settings/channels/$channelId',
          params: { channelId },
          search: (prev: Record<string, unknown>) => prev,
        } satisfies LinkComponentProps)
      : null
  }, [])

  // Handle method selection from CommandList
  const handleMethodSelect = useCallback(
    (selectedMethods: string[]) => {
      updateFilters({ methods: selectedMethods.length > 0 ? selectedMethods : undefined })
    },
    [updateFilters],
  )

  return (
    <SettingsContent spacing="sm" className="w-full">
      <div className="flex justify-between gap-3">
        <div className="flex max-w-[300px] shrink-0 grow items-center gap-3">
          <Search
            placeholder="Filter channels by name or number"
            onSearch={(value) => update({ query: value || undefined, page: 1 })}
            className="w-auto max-w-md shrink grow"
            defaultValue={query}
          />
        </div>
        <div className="flex shrink-0 grow items-center justify-end gap-3">
          <CommandPopover
            label="Types"
            collection={FilterCollection.Methods}
            selected={methods}
          >
            <MethodCommandList multiselect onSelect={handleMethodSelect} />
          </CommandPopover>
          {/*
          <Select
            value={ciEnabled === undefined ? 'All' : ciEnabled ? 'enabled' : 'disabled'}
            onValueChange={(value) =>
              updateFilters({
                ciEnabled:
                  value === 'enabled' ? true : value === 'disabled' ? false : undefined,
              })
            }
          >
            <SelectTrigger variant="ghost" size="sm-wd" className="w-30">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="All">All CI</SelectItem>
              <SelectItem value="enabled">CI enabled</SelectItem>
              <SelectItem value="disabled">CI disabled</SelectItem>
            </SelectContent>
          </Select>*/}

          <Select
            value={
              autoQAEnabled === undefined ? 'All' : autoQAEnabled ? 'enabled' : 'disabled'
            }
            onValueChange={(value) =>
              updateFilters({
                autoQAEnabled:
                  value === 'enabled' ? true : value === 'disabled' ? false : undefined,
              })
            }
          >
            <SelectTrigger
              variant="ghost"
              size="sm-wd"
              className="w-60"
              data-isdirty={autoQAEnabled !== undefined}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="All">
                <span className="text-accent-foreground">QA Enabled</span>
              </SelectItem>
              <SelectItem value="enabled">Show Auto QA enabled</SelectItem>
              <SelectItem value="disabled">Show Auto QA disabled</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <Table table={table} getRowLink={getRowLink} />

      <SettingsPagination
        pageSize={pageSize}
        totalRecords={total}
        currentPage={currentPage}
      />
    </SettingsContent>
  )
}
